import React, { useCallback, useEffect, useMemo, useState } from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FormSection from "../../Forms/FormSection/form-section";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import ConfigFormIcon from "../ConfigFormIcon";
import { useDispatch } from "react-redux";
import { TranskodeGenericOptionsProfile } from "../../../Core/Api/Types/transkode-generic-options-profile";
import { mediaService } from "../../../Core/Api/services";
import { DbMediaResourceInfos } from "../../../Core/Api/Types/db-media-resource-infos";
import { Alert, Autocomplete, Grid, InputLabel, OutlinedInput } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { EditAndConcatCustomEdl } from "../../../Core/Api/Types/edit-and-concat-custom-edl";
import { EditAndConcatCustomEdlPosition } from "../../../Core/Api/Types/edit-and-concat-custom-edl-position";
import {
  SettingRootField,
  TgopField,
  UpdateItemSettingsRootOnBasket,
  UpdateItemSettingsTgopOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { TranskodeBundleComponentType } from "../../../Core/Api/Types/transkode-bundle-component-type";
import { produce } from "immer";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
import { MediaResourceType } from "../../../Core/Api/Types/media-resource-type";
import { SystemFieldsTagNames } from "../../../Core/Api/Types/db-metadata-field-infos";

interface ConfigCommonSectionFormStitchingProps {
  item: DbOrderBasketItemInfos;
  currentComponentType?: TranskodeBundleComponentType;
  onEdlChange?: (edl: EditAndConcatCustomEdl, componentType: TranskodeBundleComponentType) => void;
}

const ConfigCommonSectionFormStitching: React.FC<ConfigCommonSectionFormStitchingProps> = (props) => {
  const { t } = useTranslation();
  const { item, currentComponentType, onEdlChange } = props;
  const dispatch = useDispatch();
  const [res, setRes] = useState<DbMediaResourceInfos[]>([]);
  const [currentComp, setCurrentComp] = useState<TranskodeBundleComponentType | null>(currentComponentType ?? null);
  const [isLogoStitchingValid, setIsLogoStitchingValid] = useState<boolean | undefined>(true);
  const [edls, setEdls] = useState<
    { componentType: TranskodeBundleComponentType; editAndConcatCustomEdl: EditAndConcatCustomEdl }[]
  >(
    item.selectedProduct?.type === OrderProductType.TranscodeBundleByProfile
      ? item.selectedProduct?.settings.components.map((item) => ({
          componentType: item?.type,
          editAndConcatCustomEdl: item?.transcodeGenericOptionsProfile?.editAndConcatCustomEdl,
        }))
      : null
  );

  // Tgop correctly typed here
  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const tgop: TranskodeGenericOptionsProfile = item.selectedProduct?.settings?.tgop;

  // Update tgop or asinput product (depending of item type) object into redux
  const handleChange = useCallback(
    (position: "before" | "after", resource: DbMediaResourceInfos | null) => {
      let alreadyHasLogo = item.assetInfos?.metadata?.some(
        (m) => m.fieldName === SystemFieldsTagNames.logo2012Presence
      );

      setIsLogoStitchingValid(
        alreadyHasLogo &&
          (resource === null || resource?.type !== MediaResourceType.Logo) &&
          (position === "before"
            ? afterValue === null || afterValue?.type !== MediaResourceType.Logo
            : beforeValue === null || beforeValue?.type !== MediaResourceType.Logo)
      );

      let conf =
        item.selectedProduct?.type === OrderProductType.TranscodeGenericSingleFile ||
        item.selectedProduct?.type === OrderProductType.DcpReferenceFileSimple ||
        item.selectedProduct?.type === OrderProductType.DcpReferenceFileStitched
          ? item.selectedProduct.settings.editAndConcatCustomEdl
          : item.selectedProduct?.type === OrderProductType.TranscodeBundleByProfile
          ? edls.find((x) => x.componentType === currentComp)!.editAndConcatCustomEdl
          : tgop?.editAndConcatCustomEdl;

      let edl: EditAndConcatCustomEdl = conf
        ? JSON.parse(JSON.stringify(conf))
        : new EditAndConcatCustomEdl({ segments: [] });

      let pos =
        position === "before"
          ? EditAndConcatCustomEdlPosition.BeforeProgram
          : EditAndConcatCustomEdlPosition.AfterProgram;

      edl.segments = edl.segments!.filter((x) => x.position !== pos);

      if (resource !== null) {
        // Add segment
        edl.segments = edl.segments!.filter((x) => x.position !== pos);

        edl.segments?.push({
          mediaResourceId: resource.id,
          mediaResourceType: resource.type,
          position: pos,
          durationFrames: 0,
          name: resource.name,
        });
      }

      if (
        item.selectedProduct?.type === OrderProductType.TranscodeGenericSingleFile ||
        item.selectedProduct?.type === OrderProductType.DcpReferenceFileSimple ||
        item.selectedProduct?.type === OrderProductType.DcpReferenceFileStitched
      )
        dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: SettingRootField.CustomEdl, value: edl }));
      else if (item.selectedProduct?.type === OrderProductType.TranscodeBundleByProfile) {
        setEdls(
          produce((draft) => {
            draft!.find((x) => x.componentType === currentComp)!.editAndConcatCustomEdl = edl;
          })
        );

        onEdlChange && onEdlChange(edl, currentComponentType!);
      } else dispatch(UpdateItemSettingsTgopOnBasket({ itemId: item.id, field: TgopField.CustomEdl, value: edl }));
    },
    [item, tgop?.editAndConcatCustomEdl, edls, currentComp]
  );

  const getValue = (value: EditAndConcatCustomEdlPosition) => {
    let result = res.find(
      (x) =>
        x.id ===
        edls
          .find((x) => x.componentType === currentComponentType)!
          .editAndConcatCustomEdl?.segments?.find((e) => e.position === value)?.mediaResourceId
    );

    return result ? result : null;
  };

  useEffect(() => {
    mediaService.GetMediaResources().then((response) => {
      if (response && response.resources) setRes(response.resources);
    });
  }, [item.id]);

  useEffect(() => {
    if (item.selectedProduct?.type === OrderProductType.TranscodeBundleByProfile) setCurrentComp(currentComponentType!);
  }, [currentComponentType]);

  const beforeValue = useMemo(
    () =>
      item.selectedProduct?.type === OrderProductType.TranscodeBundleByProfile
        ? getValue(EditAndConcatCustomEdlPosition.BeforeProgram)
        : res.find(
            (x) =>
              x.id ===
              (item.selectedProduct?.type !== OrderProductType.TranscodeGenericSingleFile
                ? tgop?.editAndConcatCustomEdl?.segments?.find(
                    (e) => e.position === EditAndConcatCustomEdlPosition.BeforeProgram
                  )?.mediaResourceId
                : item.selectedProduct.settings.editAndConcatCustomEdl?.segments?.find(
                    (e) => e.position === EditAndConcatCustomEdlPosition.BeforeProgram
                  )?.mediaResourceId)
          ),
    [item, res, tgop]
  );

  const afterValue = useMemo(
    () =>
      item.selectedProduct?.type === OrderProductType.TranscodeBundleByProfile
        ? getValue(EditAndConcatCustomEdlPosition.AfterProgram)
        : res.find(
            (x) =>
              x.id ===
              (item.selectedProduct?.type !== OrderProductType.TranscodeGenericSingleFile
                ? tgop?.editAndConcatCustomEdl?.segments?.find(
                    (e) => e.position === EditAndConcatCustomEdlPosition.AfterProgram
                  )?.mediaResourceId
                : item.selectedProduct.settings.editAndConcatCustomEdl?.segments?.find(
                    (e) => e.position === EditAndConcatCustomEdlPosition.AfterProgram
                  )?.mediaResourceId)
          ),
    [item, res, tgop]
  );

  const statusText = useMemo(() => {
    if (!beforeValue && !afterValue) return t("form:statusText.configurationIsOptional");

    const contents: string[] = [];

    if (beforeValue) contents.push(`Before: ${beforeValue.name}`);
    if (afterValue) contents.push(`After: ${afterValue.name}`);

    return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);
  }, [beforeValue, afterValue]);

  return (
    <>
      {res.length > 1 &&
        (item.selectedProduct?.type === OrderProductType.TranscodeBundleByProfile
          ? item.selectedProduct?.settings?.components
              .find((x) => x.type === currentComponentType)
              .assets?.some((x) => x.type === ObjectType.Video)
          : true) && (
          <FormSection
            icon={<ConfigFormIcon icon="stitching" />}
            title={t("common:stitching")}
            status={
              (tgop?.editAndConcatCustomEdl?.segments?.length ?? 0) ||
              (item.selectedProduct?.settings.editAndConcatCustomEdl?.segments?.length ?? 0) > 0
                ? "complete"
                : "todo"
            }
            statusText={statusText}
          >
            <Grid container spacing={1}>
              <Grid item md={6}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.insertBeforeProgram")}</InputLabel>
                <Autocomplete
                  options={res}
                  getOptionLabel={(option: any) => option.name}
                  isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                  filterOptions={(x, s) => x.filter((a) => a.name?.includesAllWords(s.inputValue))}
                  value={beforeValue}
                  onChange={(e, v) => handleChange("before", v)}
                  popupIcon={<ExpandMoreRoundedIcon />}
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                      placeholder={t("field:placeholder.selectAnyResource")}
                    />
                  )}
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item md={6}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.insertAfterProgram")}</InputLabel>
                <Autocomplete
                  options={res}
                  getOptionLabel={(option: any) => option.name}
                  isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                  filterOptions={(x, s) => x.filter((a) => a.name?.includesAllWords(s.inputValue))}
                  value={afterValue}
                  onChange={(e, v) => handleChange("after", v)}
                  popupIcon={<ExpandMoreRoundedIcon />}
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                      placeholder={t("field:placeholder.selectAnyResource")}
                    />
                  )}
                  sx={{ width: "100%" }}
                />
              </Grid>
              {!isLogoStitchingValid && (
                <Alert sx={{ mt: 1 }} color="warning" icon={<ErrorOutlineIcon />}>
                  {t("alert:warning.logoStitchingIsNotCompatible")}
                </Alert>
              )}
            </Grid>
          </FormSection>
        )}
    </>
  );
};

export default ConfigCommonSectionFormStitching;
